import { React, useState, useRef, useEffect, useLayoutEffect } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';

import FetchModule from '../../Model/Share/Network/FetchModule';

import { ReactComponent as LeftArrowSvg } from '../../image/LeftArrow.svg';
import { ReactComponent as CheckSvg } from '../../image/Check.svg';

export default function NewCoachSelectBox(props) {
  const { value, setValue, teacherId, setTeacherId } = props;
  const history = useHistory();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

	const [isClicked, setIsClicked] = useState(false);
  const [options, setOptions] = useState([]);
  const [visibleCount, setVisibleCount] = useState(0);

  const TopLevelRef = useRef();
	const SelectRef = useRef();
  const MeasureRef = useRef();
  const OptionRef = useRef();

  useEffect(() => {
    setOptions([]);
    (
      async () => {
        const fetchModule = new FetchModule();
        const data = await fetchModule.getData('coach/select/', 'GET');
        
        if (data.status === 200) setOptions(data.data);
        else if (data.status === 401) history.push('/');
        else {};
      }
    )();
  }, [history])

	useEffect(() => {
		if (options.length === 0 || teacherId === -1) return;
    
    const teacherIdList = teacherId.split(',');

    let newValue = [];

    for (let i = 0; i < teacherIdList.length; i++) {
      newValue.push(options[options.findIndex(j => j.id === parseInt(teacherIdList[i]))])
    }
    
    setValue(newValue);
	}, [options, teacherId])

  // 부모(ColorBoxTotalWrapper) 너비 안에 들어가는 chip 개수 계산 //
  useLayoutEffect(() => {
    function recalc() {
      const wrap = OptionRef.current;
      const measure = MeasureRef.current;

      if (!wrap || !measure) return;

      const W = wrap.clientWidth;
      const children = Array.from(measure.children);

      if (children.length === 0 || value.length === 0) {
        setVisibleCount(0);
        return;
      }

      const GAP = 5;
      const chipNodes = children.slice(0, value.length);
      const badgeNode = children[children.length - 1];
      const widths = chipNodes.map(n => n.getBoundingClientRect().width);
      const badgeW = badgeNode.getBoundingClientRect().width;

      const totalAll = widths.reduce((a, b) => a + b, 0) + Math.max(0, widths.length - 1) * GAP;

      if (totalAll <= W) {
        setVisibleCount(value.length);
        return;
      }

      let acc = 0;
      let n = 0;

      for (let i = 0; i < widths.length; i++) {
        const next = acc + widths[i] + (n > 0 ? GAP : 0);
        const withBadge = next + GAP + badgeW;

        if (withBadge <= W) {
          acc = next;
          n++;
        }
        else break;
      }

      setVisibleCount(n);
    }

    recalc();

    const wrap = OptionRef.current;

    if (!wrap) return;

    const ro = new ResizeObserver(recalc);
    ro.observe(wrap);

    return () => ro.disconnect();
  }, [value, isDark]);

  // 컴포넌트 밖을 클릭하면 셀렉트 창 닫기 //
  useEffect(() => {
    document.addEventListener("mousedown", onClickOutSide);

    return () => {
      document.removeEventListener("mousedown", onClickOutSide);
    }
  }, [])

  // 컴포넌트 밖을 클릭하면 셀렉트 창 닫기 //
  function onClickOutSide(e) {
    if (TopLevelRef.current !== e.target && !TopLevelRef.current.contains(e.target)) {
      SelectRef.current.style.border = '0px';
      SelectRef.current.style.height = `0px`;
      SelectRef.current.style.overflow = 'hidden';
      setIsClicked(false);
    }
  }

	function onClick() {
		if (isClicked) {
			SelectRef.current.style.border = '0px';
			SelectRef.current.style.height = `0px`;
			SelectRef.current.style.overflow = 'hidden';
			setIsClicked(false);
		}
		else {
			SelectRef.current.style.border = `1px solid ${theme.line}`;
			SelectRef.current.style.height = (32 * options.length) > 130 ? `130px` : ((32 * options.length) + 'px');
			SelectRef.current.style.overflowY = 'scroll';
			SelectRef.current.style.overflowX = 'none';
			setIsClicked(true);
		}
	}

  // 코치 클릭시 //
	function optionalClick(index) {
    let newDefaultVal = value.slice();

    if (newDefaultVal.includes(options[index])) {
      let deletedIndex = 0;

      newDefaultVal.filter(function(item, idx) {
        if (options[index] === item) {
          deletedIndex = idx;
          return;
        }
      })

      newDefaultVal.splice(deletedIndex, 1);
      setValue(newDefaultVal);
    }
    else {
      newDefaultVal.push(options[index]);
      setValue(newDefaultVal);
      setTeacherId(-1);
    }
	}

	return (
    <TopLevelWrapper ref={TopLevelRef}>
      <DefaultView 
        isClicked={isClicked}
        onClick={onClick}>
        <ColorBoxTotalWrapper ref={OptionRef}>
          <ColorBoxWrapper>
            {value.slice(0, visibleCount).map((data, index) => (
              <ColorMapper key={index}>
                <ColorBox>
                  <ProfileWrapper>
                    <ProfileImage src={data.profile || 'https://picture.link-zone.org/ZoneLogo.png'} />
                    <ProfileName>{data.name}</ProfileName>
                  </ProfileWrapper>
                </ColorBox>
              </ColorMapper>
            ))}
            {
              visibleCount < value.length &&
              <ColorMapper>
                <ColorBox>
                  <ProfileNumberBox>{`+${value.length - visibleCount}`}</ProfileNumberBox>
                </ColorBox>
              </ColorMapper>
            }
          </ColorBoxWrapper>
          <MeasureLayer ref={MeasureRef} aria-hidden>
            {value.map((data, index) => (
              <ColorMapper key={'measure-' + index}>
                <ColorBox>
                  <ProfileWrapper>
                    <ProfileImage src={data.profile || 'https://picture.link-zone.org/ZoneLogo.png'} />
                    <ProfileName>{data.name}</ProfileName>
                  </ProfileWrapper>
                </ColorBox>
              </ColorMapper>
            ))}
            <ColorMapper>
              <ColorBox>
                <ProfileNumberBox>{`+${value.length}`}</ProfileNumberBox>
              </ColorBox>
            </ColorMapper>
          </MeasureLayer>
        </ColorBoxTotalWrapper>
        <StyledArrow isClicked={isClicked} />
      </DefaultView>
      <SelectBox
        ref={SelectRef}
        isClicked={isClicked}>
        {
          options.map((data, index) =>
            <OptionWrapper key={index} isClicked={value.includes(data)} onClick={() => optionalClick(index)}>
              <Option>{data.name}</Option>
              { value.includes(data) && <StyledCheck /> }
            </OptionWrapper>
          )
        }
      </SelectBox>
    </TopLevelWrapper>
  )
}

const TopLevelWrapper = styled.div`
	display: flex;
	flex-direction: column;
	
	// width: 344px;
  width: 100%;
	height: 35px;
`;

const DefaultView = styled.div`
  display: flex;
	flex-direction: row;

  justify-content: center;
  align-items: center;

  padding: 0 8px 0 1px;

	// width: 328px;
  width: calc(100% - 12px);
	height: 35px;
  
  z-index: 3;

	border: 1px solid ${props => props.theme.line};
	border-radius: 8px;

	background-color: ${props => props.theme.subBackground};
  
  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
  }
`;

const StyledArrow = styled(LeftArrowSvg)`
  width: 10px;
  height: auto;
  flex-shrink: 0;

  color: ${props => props.theme.subText};
  transform: ${props => props.isClicked ? `rotate(90deg)` : `rotate(-90deg)`};
  transition: transform 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
  }
`;

const StyledCheck = styled(CheckSvg)`
  width: 12px;
  height: 9px;
  color: ${props => props.theme.primary};
`;

const SelectBox = styled.div`
	position: absolute;
	display: flex;
	flex-direction: column;

	justify-content: flex-start;
	align-items: flex-start;

  margin-top: 40px;

	// width: 328px;
  // width: calc(45.45% - 18px);
  width: calc(45.45% - 2px);
	height: 0px;

  // padding: 0 8px 0 0;

  z-index: 2;

  border: 0px;
	border-radius: 8px;
	overflow: hidden;

	background-color: ${props => props.theme.subBackground};

  transition: all 0.3s ease-in-out;

	&:hover {
		cursor: pointer;
	}

	&::-webkit-scrollbar {
		// width: 9px;
    width: 0px;
	}

	&::-webkit-scrollbar-thumb {
    box-shadow: inset 0 0 10px 20px ${props => props.theme.primary};
		border: 3px solid transparent;
		height: 25px;
		border-radius: 200px;
	}

	&::-webkit-scrollbar-track {
    box-shadow: inset 0 0 10px 10px transparent;
		border: 4px solid transparent;
		background-color: transparent;
	}

	&::-webkit-scrollbar-track-piece {
		background-color: transparent;
	}
`;

const ColorBoxTotalWrapper = styled.div`
	display: flex;
	flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  min-width: 0;
	width: calc(100% - 16px);

	&:hover {
		cursor: pointer;
	}
`;

const MeasureLayer = styled.div`
  position: absolute;
  visibility: hidden;
  pointer-events: none;

  display: flex;
  flex-direction: row;

  white-space: nowrap;

  top: -9999px;
  left: -9999px;

  gap: 5px;
`;

const OptionWrapper = styled.div`
	display: flex;
	flex-direction: row;

  justify-content: space-between;
  align-items: center;

	width: calc(100% - 16px);
  min-height: 32px;

  padding: 0 7px 0 7px;

  border: 1px solid transparent;
  border-bottom: 1px solid ${props => props.theme.line};
  color: ${props => props.isClicked ? props.theme.primary : props.theme.text};

  &:first-child {
    border-radius: 7px 7px 0 0;
  }

  &:last-child {
    border-radius: 0 0 7px 7px;
  }

	&:hover {
		cursor: pointer;
    color: ${props => props.isClicked ? props.theme.primary : props.theme.white};
    border: 1px solid ${props => props.theme.primary};
	}

  transition: all 0.3s ease-in-out;
`;

const Option = styled.p`
	font-family: NotoSansKR-Regular;
	font-size: 14px;

  margin: 0 0 0 0;
  padding: 0 0 0 0;
`;

const ColorBoxWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 5px;
`;

const ColorMapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;
`;

const ColorBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  padding: 0 10px 0 0;

  height: 21px;

  border-radius: 3px;
  background-color: ${props => props.theme.buttonFill};
`;

const ProfileWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 5px;
`;

const ProfileImage = styled.img`
  width: 21px;
  height: 21px;

  border-radius: 3px 0 0 3px;
  object-fit: cover;
  flex-shrink: 0;
`;

const ProfileName = styled.span`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.theme.text};

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ProfileNumberBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  padding: 0 5px;

  height: 21px;

  border-radius: 5px;
  background-color: ${props => props.theme.primary};

  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.theme.subBackground};
`;

const ProfileNumber = styled.div`
  padding: 0 5px;

  height: 22px;

  border-radius: 5px;
  background-color: ${props => props.theme.primary};

  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.theme.subBackground};
`;
